<script setup lang="ts">
// The waste map: the season's visits dropped into H3 res-8 cells, filled by a
// 3×3 bivariate grid — treated share is the row, visit volume the column. The
// server does the counting and classing; this component projects the returned
// cells, paints each class from the shared palette, and turns the same grid
// into the filter control: every swatch is a button that narrows the spec to
// that class's dissolved geometry.
import { computed, ref } from "vue";

import { WASTE_BIV, WASTE_THIN_COLOR } from "@/lib/mapReviewLayers";
import type { MapReviewWasteView } from "@/type/api";

const props = defineProps<{
	/** The waste payload; null until the first read lands. */
	view: MapReviewWasteView | null;
	/** The class currently filtering the spec, or null for none. */
	selectedClass: number | null;
	/** Whether the cells are already on the workbench map. The parent owns the
	 * layer; without it the toggle keeps its own reading. */
	onMap?: boolean;
}>();

const emit = defineEmits<{
	"select-class": [index: number];
	"send-to-map": [on: boolean];
}>();

// One map in the rail, so a fixed clip id is safe and the boundary and the cell
// layer share it.
const CLIP_ID = "waste-map-clip";
const WIDTH = 420;
const PAD = 8;
/** Fewer visits than this cannot be judged; the server leaves their class null
 * and the map draws them pale. */
const TOO_THIN = 5;
const VOLUME_NAMES = ["few", "medium", "many"] as const;

type LonLat = [number, number];

interface Projection {
	width: number;
	height: number;
	x: (lon: number) => number;
	y: (lat: number) => number;
}

function fmt(value: number): string {
	return value.toLocaleString();
}

function percent(value: number): string {
	return Math.round(value).toLocaleString();
}

function sharePercent(visits: number, treated: number): number {
	return visits > 0 ? Math.round((100 * treated) / visits) : 0;
}

// An equirectangular projection scaled by the mid-latitude cosine: the same
// framing the preview uses, which keeps the service area's shape right at
// district scale without pulling in a tile library for a panel-sized drawing.
const projection = computed<Projection | null>(() => {
	const view = props.view;
	if (!view) return null;
	const points: LonLat[] = [];
	for (const polygon of view.h3.boundary?.polygons ?? []) {
		for (const ring of polygon) points.push(...ring);
	}
	// A payload with no boundary still has cells to draw; the cell rings bound
	// the same field. The outline simply has nothing to trace.
	if (points.length === 0) {
		for (const cell of view.h3.cells) points.push(...cell.boundary);
	}
	if (points.length === 0) return null;

	let lon0 = Number.POSITIVE_INFINITY;
	let lon1 = Number.NEGATIVE_INFINITY;
	let lat0 = Number.POSITIVE_INFINITY;
	let lat1 = Number.NEGATIVE_INFINITY;
	for (const [lon, lat] of points) {
		if (lon < lon0) lon0 = lon;
		if (lon > lon1) lon1 = lon;
		if (lat < lat0) lat0 = lat;
		if (lat > lat1) lat1 = lat;
	}
	const kx = Math.cos(((lat0 + lat1) / 2) * (Math.PI / 180));
	const spanX = (lon1 - lon0) * kx;
	const spanY = lat1 - lat0;
	if (!(spanX > 0) || !(spanY > 0)) return null;

	// One scale for both axes: `inner` pixels across the cosine-corrected span.
	// Dividing each axis by its own span instead would stretch the field by
	// 1/cos(lat) — a quarter at this latitude — and draw the eastern half past
	// the viewBox, where the svg clips it.
	const inner = WIDTH - 2 * PAD;
	const scale = inner / spanX;
	return {
		width: WIDTH,
		height: Math.round(spanY * scale) + 2 * PAD,
		x: (lon: number) => PAD + (lon - lon0) * kx * scale,
		y: (lat: number) => PAD + (lat1 - lat) * scale,
	};
});

/** The service-area outline, one closed subpath per ring. Empty when the
 * payload carries no boundary; the cells still draw. */
const districtPath = computed(() => {
	const view = props.view;
	const project = projection.value;
	if (!view || !project) return "";
	let d = "";
	for (const polygon of view.h3.boundary?.polygons ?? []) {
		for (const ring of polygon) {
			ring.forEach(([lon, lat], index) => {
				d += `${index === 0 ? "M" : "L"}${project.x(lon).toFixed(1)},${project.y(lat).toFixed(1)}`;
			});
			d += "Z";
		}
	}
	return d;
});

function fillFor(cls: number | null): string {
	if (cls == null || cls < 0 || cls > 8) return WASTE_THIN_COLOR;
	return WASTE_BIV[Math.floor(cls / 3)][cls % 3];
}

interface CellShape {
	key: string;
	points: string;
	fill: string;
	title: string;
}

const cells = computed<CellShape[]>(() => {
	const view = props.view;
	const project = projection.value;
	if (!view || !project) return [];
	return view.h3.cells.map((cell) => {
		const thin = cell.class == null && cell.visits < TOO_THIN;
		return {
			key: cell.h,
			points: cell.boundary
				.map(
					([lon, lat]) =>
						`${project.x(lon).toFixed(1)},${project.y(lat).toFixed(1)}`,
				)
				.join(" "),
			fill: fillFor(cell.class),
			title:
				`${fmt(cell.visits)} visits · ${fmt(cell.treated)} treated · ` +
				`${sharePercent(cell.visits, cell.treated).toLocaleString()}% treated` +
				(thin ? " · too few visits to judge" : ""),
		};
	});
});

/** How many cells the payload carries per class. A class with no cells cannot
 * narrow anything, so its swatch is inert rather than posting an empty
 * MultiPolygon the spec would refuse. While a class filter is active the
 * payload is that class's cells alone, which is why the other swatches are
 * inert until the active one is cleared. */
const classCounts = computed<number[]>(() => {
	const counts = new Array<number>(9).fill(0);
	for (const cell of props.view?.h3.cells ?? []) {
		if (cell.class != null && cell.class >= 0 && cell.class <= 8) {
			counts[cell.class] += 1;
		}
	}
	return counts;
});

/** The conversion axis, top row first — high conversion is class row 0 — with
 * the treated-share range printed beside it. */
const legendRows = computed(() => {
	const [low, high] = props.view?.breaks.treatedShare ?? [0, 0];
	return [
		{ row: 0, name: "high conversion", range: `≥ ${percent(high)}%` },
		{
			row: 1,
			name: "middling",
			range: `${percent(low)}–${percent(high)}%`,
		},
		{ row: 2, name: "low conversion", range: `< ${percent(low)}%` },
	];
});

/** The visit-volume axis, left column first, printed beneath the swatches. */
const volumeRanges = computed(() => {
	const [low, high] = props.view?.breaks.visits ?? [0, 0];
	return [
		`< ${fmt(Math.round(low))}`,
		`${fmt(Math.round(low))}–${fmt(Math.round(high))}`,
		`≥ ${fmt(Math.round(high))}`,
	];
});

/** The scoreboard under the legend: the payload's breadth and the waste corner
 * — class 8, many visits and low conversion. */
const summary = computed(() => {
	const all = props.view?.h3.cells ?? [];
	let visited = 0;
	let judged = 0;
	let corner = 0;
	let cornerVisits = 0;
	let cornerTreated = 0;
	for (const cell of all) {
		if (cell.visits > 0) visited += 1;
		if (cell.visits >= TOO_THIN) judged += 1;
		if (cell.class === 8) {
			corner += 1;
			cornerVisits += cell.visits;
			cornerTreated += cell.treated;
		}
	}
	return {
		visited,
		judged,
		corner,
		cornerVisits,
		cornerTreated,
		cornerShare: sharePercent(cornerVisits, cornerTreated),
	};
});

// The toggle is the component's reading of the action; the parent owns the
// layer. When it passes its state back the label follows the map, and the emit
// is still the only way the layer changes.
const localOnMap = ref(false);
const mapOn = computed(() => props.onMap ?? localOnMap.value);

function toggleOnMap(): void {
	const next = !mapOn.value;
	localOnMap.value = next;
	emit("send-to-map", next);
}

function selectClass(index: number): void {
	// An empty class would post an empty MultiPolygon, which the spec refuses.
	if ((classCounts.value[index] ?? 0) === 0) return;
	emit("select-class", index);
}
</script>

<template>
	<section class="waste-map">
		<header class="map-head">
			<p v-if="view" class="map-caption">
				H3 res {{ view.h3.resolution }} · {{ view.year }} season<br />
				visit volume × treated share
			</p>
			<p v-else class="map-caption">Waste map</p>
			<button
				type="button"
				class="map-send"
				:class="{ on: mapOn }"
				:aria-pressed="mapOn"
				:disabled="!view"
				@click="toggleOnMap"
			>
				{{ mapOn ? "Remove from map" : "Send to map" }}
			</button>
		</header>

		<p v-if="!view" class="map-empty">
			The waste map has not loaded for the current filter.
		</p>

		<svg
			v-else-if="projection"
			class="map-svg"
			:viewBox="`0 0 ${projection.width} ${projection.height}`"
			role="img"
			aria-label="H3 cells filled by visit volume and treated share"
		>
			<defs>
				<clipPath :id="CLIP_ID">
					<path :d="districtPath" clip-rule="evenodd" />
				</clipPath>
			</defs>
			<g :clip-path="`url(#${CLIP_ID})`">
				<polygon
					v-for="cell in cells"
					:key="cell.key"
					:points="cell.points"
					:fill="cell.fill"
					stroke="#ffffff"
					stroke-width="0.3"
				>
					<title>{{ cell.title }}</title>
				</polygon>
			</g>
			<path
				v-if="districtPath"
				:d="districtPath"
				fill="none"
				fill-rule="evenodd"
				stroke="#1c1f23"
				stroke-width="1.3"
				stroke-linejoin="round"
			/>
		</svg>

		<p v-else class="map-empty">
			The payload carries no boundary or cell geometry to draw.
		</p>

		<div
			v-if="view"
			class="legend"
			aria-label="Bivariate class legend and filter"
		>
			<div class="legend-grid">
				<span aria-hidden="true"></span>
				<span
					v-for="name in VOLUME_NAMES"
					:key="`column-${name}`"
					class="legend-column"
					>{{ name }}</span
				>
				<template v-for="row in legendRows" :key="row.row">
					<span class="legend-row">
						<span class="legend-name">{{ row.name }}</span>
						<span class="legend-range">{{ row.range }}</span>
					</span>
					<button
						v-for="(name, col) in VOLUME_NAMES"
						:key="`${row.row}-${name}`"
						type="button"
						class="legend-swatch"
						:class="{
							selected: selectedClass === row.row * 3 + col,
						}"
						:style="{ background: WASTE_BIV[row.row][col] }"
						:disabled="classCounts[row.row * 3 + col] === 0"
						:title="
							classCounts[row.row * 3 + col] === 0
								? `${row.name}, ${name} visits — no cells in scope`
								: `${row.name}, ${name} visits`
						"
						:aria-label="`${row.name}, ${name} visits`"
						:aria-pressed="selectedClass === row.row * 3 + col"
						@click="selectClass(row.row * 3 + col)"
					></button>
				</template>
			</div>
			<div class="legend-breaks">
				<span v-for="label in volumeRanges" :key="label">{{ label }}</span>
			</div>
			<p class="map-summary">
				{{ fmt(summary.visited) }} cells carry a visit;
				{{ fmt(summary.judged) }} carry at least {{ TOO_THIN }}.
				{{ fmt(summary.corner) }} cells are both busy and converting poorly —
				{{ fmt(summary.cornerVisits) }} visits,
				{{ fmt(summary.cornerTreated) }} of them treated (<b
					>{{ percent(summary.cornerShare) }}%</b
				>).
			</p>
			<p class="legend-note">
				Pale cells carry fewer than {{ TOO_THIN }} visits and are never
				coloured; every swatch narrows the selection, and clicking the selected
				class clears it.
			</p>
		</div>
	</section>
</template>

<style scoped>
.waste-map {
	display: flex;
	flex-direction: column;
	grid-column: 1 / -1;
	min-width: 0;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
}

.map-head {
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin-bottom: 4px;
}

.map-caption {
	flex: 1 1 auto;
	min-width: 0;
	margin: 0;
	font-size: 0.7rem;
	line-height: 1.35;
	color: #6c757d;
}

.map-send {
	flex: 0 0 auto;
	border: 1px solid #adb5bd;
	background: #fff;
	border-radius: 3px;
	padding: 1px 7px;
	font-size: 0.68rem;
	line-height: 1.5;
	color: #343a40;
	cursor: pointer;
}

.map-send:hover {
	border-color: #0d6efd;
	color: #0d6efd;
}

.map-send.on {
	border-color: #1864ab;
	background: #e7f5ff;
	color: #1864ab;
}

.map-send:disabled {
	border-color: #dee2e6;
	color: #adb5bd;
	cursor: default;
}

.map-svg {
	display: block;
	width: 100%;
	height: auto;
	max-height: 460px;
}

.map-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 140px;
	margin: 0;
	padding: 0 8px;
	text-align: center;
	font-size: 0.75rem;
	color: #adb5bd;
}

/* The legend is the control: the grid reads as conversion × volume, and every
   swatch is a button, so filter and key are the same object. */
.legend {
	margin-top: 8px;
}

.legend-grid {
	display: grid;
	grid-template-columns: minmax(0, 108px) repeat(3, minmax(0, 1fr));
	gap: 2px;
	align-items: stretch;
	width: 100%;
	max-width: 320px;
}

.legend-column {
	font-size: 0.62rem;
	color: #6c757d;
	text-align: center;
}

.legend-row {
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding-right: 6px;
	font-size: 0.64rem;
	line-height: 1.15;
	color: #6c757d;
	text-align: right;
}

.legend-name {
	color: #212529;
	font-size: 0.68rem;
}

.legend-range {
	font-size: 0.6rem;
	font-variant-numeric: tabular-nums;
}

.legend-swatch {
	height: 22px;
	border: 1px solid rgba(28, 31, 35, 0.12);
	border-radius: 2px;
	cursor: pointer;
}

.legend-swatch:hover {
	border-color: #1864ab;
}

.legend-swatch:disabled {
	opacity: 0.35;
	cursor: default;
}

.legend-swatch:disabled:hover {
	border-color: rgba(28, 31, 35, 0.12);
}

.legend-swatch.selected {
	outline: 2px solid #1864ab;
	outline-offset: 1px;
}

.legend-breaks {
	display: grid;
	grid-template-columns: minmax(0, 108px) repeat(3, minmax(0, 1fr));
	gap: 2px;
	max-width: 320px;
	font-size: 0.6rem;
	color: #6c757d;
	text-align: center;
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

.legend-breaks :first-child {
	grid-column: 2;
}

.map-summary {
	margin: 8px 0 0;
	font-size: 0.68rem;
	line-height: 1.45;
	color: #6c757d;
	font-variant-numeric: tabular-nums;
}

.map-summary b {
	color: #212529;
	font-weight: 600;
}

.legend-note {
	margin: 6px 0 0;
	font-size: 0.64rem;
	line-height: 1.4;
	color: #6c757d;
}
</style>
